import { Button, Modal } from '@/components';
import { R } from '@/features/profile/ProfileResources.resx';
import { Input } from '@/components/Input';
import { InlineAlert } from '@/components/InlineAlert';
import { ModulusWarning } from '@/features/profile/components/ModulusWarning';
import { useChildForm } from './useChildForm';
import {
    ChildModalMode,
    ChildModalProps,
} from '@/features/profile/MinorChildren/ChildModal/ChildModal.types';
import {
    isCprNumber,
    isFullName,
    isUnderCertainAge,
} from '@/shared/validators';
import * as React from 'react';
import { ReactElement } from 'react';
import { Utils } from '@/shared/Utils';

export const ChildModal = (props: ChildModalProps) => {
    const {
        register,
        handleSubmit,
        errors,
        onSubmit,
        status,
        isProcessing,
        reset,
        errorMessage,
        showModulusWarning,
        hideModulusWarning,
        getValues,
    } = useChildForm(props);

    const { mode, onClose } = props;
    const confirmButton = generateConfirmButton(props.mode, isProcessing);

    return (
        <Modal
            onClose={() => {
                reset();
                onClose();
            }}
            title={generateTitle(mode)}
            showCloseButtonInRightCorner
        >
            {showModulusWarning && (
                <ModulusWarning
                    onBack={hideModulusWarning}
                    onConfirm={onSubmit}
                    cprNumber={getValues().CPR}
                    isPending={isProcessing}
                />
            )}

            {!showModulusWarning && status !== 'error' && (
                <form onSubmit={handleSubmit(onSubmit)}>
                    <Input
                        label={R.FullName}
                        inputClassName="mb-2"
                        containerClassName="mt-8"
                        {...register('Name', {
                            required: R.PleaseEnterFullName,
                            validate: {
                                isFullName: (value: string) =>
                                    isFullName(value) || R.PleaseEnterFullName,
                            },
                        })}
                        error={errors.Name?.message}
                    />
                    <Input
                        label={R.CprNumber}
                        inputClassName="mb-2"
                        placeholder={R.CprPlaceholder}
                        {...register('CPR', {
                            required: R.CprRequired,
                            setValueAs: (value) => value.replace('-', ''),
                            validate: {
                                validCpr: (value: string) =>
                                    isCprNumber(value) || R.InvalidCpr,
                                isUnderCertainAge: (value: string) =>
                                    isUnderCertainAge(value, 18) || R.CprTooOld,
                            },
                        })}
                        error={errors.CPR?.message}
                        maxLength={11}
                        onInput={Utils.sanitizeCprInput}
                    />
                    <p
                        style={{ whiteSpace: 'pre-line' }}
                        className="whitespace-pre-line leading-normal"
                        dangerouslySetInnerHTML={{
                            __html: props.childTerms,
                        }}
                    ></p>
                    <div className="mt-6 flex flex-col gap-4 p-4">
                        <footer className="text-right">{confirmButton}</footer>
                    </div>
                </form>
            )}

            {status === 'error' && (
                <InlineAlert onClose={reset} errorMessage={errorMessage} />
            )}
        </Modal>
    );
};

const generateConfirmButton = (
    mode: ChildModalMode,
    isProcessing: boolean,
): ReactElement => {
    if (mode.isAdding) {
        return (
            <Button
                variant="primary"
                type={'submit'}
                className={isProcessing ? 'button--loading' : ''}
            >
                {R.ProfileConfirmAddKid}
            </Button>
        );
    }
    if (mode.isEditing) {
        return (
            <Button
                variant="primary"
                type={'submit'}
                className={isProcessing ? 'button--loading' : ''}
            >
                {R.ProfileConfirmEditKid}
            </Button>
        );
    }
};

const generateTitle = (mode: ChildModalMode): string => {
    if (mode.isEditing) {
        return R.Profile_EditChild;
    }
    if (mode.isAdding) {
        return R.Profile_AddChild;
    }
    return;
};
